import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import type { Drill, Practice, Coach } from '../types';

interface UseDrillsProps {
  selectedPractice: Practice | null;
}

export function useDrills({ selectedPractice }: UseDrillsProps) {
  const [drills, setDrills] = useState<Drill[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingDrill, setIsAddingDrill] = useState(false);
  const [editingDrill, setEditingDrill] = useState<Drill | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [drillCoaches, setDrillCoaches] = useState<Record<string, string[]>>({});
  
  const latestFetchRequestRef = useRef(0);

  // Fetch drills when selected practice changes
  useEffect(() => {
    if (selectedPractice?.id) {
      fetchDrills(selectedPractice.id);
      // Also fetch coaches for the team
      if (selectedPractice.team_id) {
        fetchCoaches(selectedPractice.team_id);
      }
    } else {
      setDrills([]);
    }
  }, [selectedPractice?.id]);

  // Listen for cache clear events to immediately clear drills
  useEffect(() => {
    const handleCacheClear = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { teamId, newTeamId } = customEvent.detail || {};
      
      console.log(`[useDrills] Cache clear event received for team: ${teamId || newTeamId}`);
      
      // Clear drills immediately
      setDrills([]);
    };
    
    window.addEventListener('clear-team-cache', handleCacheClear);
    
    return () => {
      window.removeEventListener('clear-team-cache', handleCacheClear);
    };
  }, []);

  // Fetch drills for a specific practice
  const fetchDrills = async (practiceId: string) => {
    if (!practiceId) return;
    const requestId = ++latestFetchRequestRef.current;

    setIsLoading(true);
    
    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Drill fetch timeout')), 15000); // 15 second timeout
      });

      const fetchPromise = (async () => {
        console.time('[Drill Loading] Total fetch time');
        
        // Step 1: Get basic drill info (fast query)
        const { data: drillsData, error: drillsError } = await supabase
          .from('drills')
          .select('id, name, duration, description, focus_area, order_index, intensity_level')
          .eq('practice_id', practiceId)
          .order('order_index', { ascending: true });

        if (drillsError) {
          console.error('Error fetching drills:', drillsError);
          if (requestId !== latestFetchRequestRef.current) return;
          setDrills([]);
          return;
        }

        // If no drills, just set empty state and exit
        if (!drillsData || drillsData.length === 0) {
          if (requestId !== latestFetchRequestRef.current) return;
          setDrills([]);
          console.timeEnd('[Drill Loading] Total fetch time');
          return;
        }

        console.time('[Drill Loading] Related data fetch');
        
        const drillIds = drillsData.map(drill => drill.id);
        
        // Run position groups and steps queries in parallel (coaching points will be lazy-loaded in DrillCard)
        const [positionGroupsResult, stepsResult] = await Promise.all([
          supabase
            .from('position_groups')
            .select('id, drill_id, group_name, assigned_coach_id')
            .in('drill_id', drillIds),
          
          supabase
            .from('drill_steps')
            .select('id, drill_id, position_group_id, name, setup, execution, equipment, video_url, order_index')
            .in('drill_id', drillIds)
            .order('order_index', { ascending: true })
        ]);

        const { data: positionGroupsData, error: pgError } = positionGroupsResult;
        const { data: stepsData, error: stepsError } = stepsResult;

        if (pgError) {
          console.error('Position groups error:', pgError);
        }
        if (stepsError) {
          console.error('Error fetching drill steps:', stepsError);
        }

        console.timeEnd('[Drill Loading] Related data fetch');
        console.time('[Drill Loading] Data transformation');

        // Update drillCoaches state
        const newDrillCoaches: Record<string, string> = {};
        positionGroupsData?.forEach(pg => {
          if (pg.assigned_coach_id) {
            newDrillCoaches[`${pg.drill_id}-${pg.group_name}`] = pg.assigned_coach_id;
          }
        });

        // Transform the data (without coaching points for now)
        const transformedDrills = drillsData.map(drill => {
          // Get position groups for this drill
          const drillPositionGroups = positionGroupsData
            ?.filter(pg => pg.drill_id === drill.id)
            .map(pg => {
              // Get steps for this position group
              const groupSteps = stepsData
                ?.filter(step => step.position_group_id === pg.id)
                .map(step => ({
                  id: step.id,
                  name: step.name,
                  setup: step.setup,
                  execution: step.execution,
                  equipment: step.equipment || '',
                  video_url: step.video_url || '',
                  coaching_points: []
                })) || [];

              // console.log(`[useDrills] Position group "${pg.group_name}" for drill "${drill.name}" has ${groupSteps.length} steps:`, 
              //   groupSteps.map(s => ({ id: s.id, name: s.name })));

              return {
                group_name: pg.group_name,
                steps: groupSteps
              };
            }) || [];

          return {
            id: drill.id,
            name: drill.name,
            duration: drill.duration,
            description: drill.description,
            focus_area: drill.focus_area,
            position_groups: drillPositionGroups,
            intensity_level: drill.intensity_level,
            // Add missing required fields with defaults
            practice_id: practiceId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            equipment_needed: [],
            instructions: '',
            difficulty_level: 'Beginner' as const,
            age_group: 'All',
            focus_areas: [],
            order_index: drill.order_index || 0,
            category: '' as any,
            tags: []
          };
        });

        console.timeEnd('[Drill Loading] Data transformation');
        console.timeEnd('[Drill Loading] Total fetch time');

        if (requestId !== latestFetchRequestRef.current) return;
        setDrills(transformedDrills as unknown as Drill[]);
        // Fetch coach assignments from database instead of using legacy format
        await fetchDrillCoachAssignments();
        console.log(`[useDrills] Successfully loaded ${transformedDrills.length} drills (coaching points will be lazy-loaded)`);
      })();

      // Race between the fetch and timeout
      await Promise.race([fetchPromise, timeoutPromise]);
    } catch (error) {
      console.error('[useDrills] Error fetching drills:', error);
      if (requestId !== latestFetchRequestRef.current) return;
      setDrills([]);
    } finally {
      if (requestId === latestFetchRequestRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Fetch coaches for the drill's team
  const fetchCoaches = async (teamId: string) => {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .select('*')
        .eq('team_id', teamId)
        .order('name');

      if (error) {
        console.error('Error fetching coaches:', error);
        return;
      }

      setCoaches(data);
    } catch (error) {
      console.error('Error fetching coaches:', error);
    }
  };

  // Fetch drill coach assignments from the database
  const fetchDrillCoachAssignments = async () => {
    if (!selectedPractice?.id) return;
    
    try {
      // Get all drills for this practice first
      const { data: practiceData, error: practiceError } = await supabase
        .from('drills')
        .select('id')
        .eq('practice_id', selectedPractice.id);

      if (practiceError) throw practiceError;
      
      if (!practiceData || practiceData.length === 0) {
        setDrillCoaches({});
        return;
      }

      const drillIds = practiceData.map(d => d.id);

      // Fetch all coach assignments for these drills
      const { data, error } = await supabase
        .from('drill_coach_assignments')
        .select('drill_id, position_group_name, coach_id')
        .in('drill_id', drillIds);

      if (error) throw error;

      // Group assignments by drill and position group
      const assignments: Record<string, string[]> = {};
      
      if (data) {
        data.forEach(assignment => {
          const key = `${assignment.drill_id}-${assignment.position_group_name}`;
          if (!assignments[key]) {
            assignments[key] = [];
          }
          assignments[key].push(assignment.coach_id);
        });
      }

      setDrillCoaches(assignments);
    } catch (error) {
      console.error('Error fetching drill coach assignments:', error);
    }
  };

  // Update coach assignment for a drill's position group
  const handleCoachChange = async (drillId: string, positionGroupName: string, coachId: string, action: 'add' | 'remove' | 'clear') => {
    try {
      const currentCoaches = drillCoaches[`${drillId}-${positionGroupName}`] || [];
      let newCoaches: string[] = [];
      
      // Perform database operations based on action
      switch (action) {
        case 'add':
          if (!currentCoaches.includes(coachId)) {
            // Add coach assignment to database
            const { error } = await supabase
              .from('drill_coach_assignments')
              .insert({
                drill_id: drillId,
                position_group_name: positionGroupName,
                coach_id: coachId
              });
            
            if (error) {
              console.error('Error adding coach assignment:', error);
              toast.error('Failed to assign coach');
              return;
            }
            
            newCoaches = [...currentCoaches, coachId];
          } else {
            toast.error('Coach is already assigned to this position group');
            return;
          }
          break;
          
        case 'remove':
          // Remove coach assignment from database
          const { error: removeError } = await supabase
            .from('drill_coach_assignments')
            .delete()
            .eq('drill_id', drillId)
            .eq('position_group_name', positionGroupName)
            .eq('coach_id', coachId);
          
          if (removeError) {
            console.error('Error removing coach assignment:', removeError);
            toast.error('Failed to remove coach assignment');
            return;
          }
          
          newCoaches = currentCoaches.filter(id => id !== coachId);
          break;
          
        case 'clear':
          // Remove all coach assignments for this drill position group from database
          const { error: clearError } = await supabase
            .from('drill_coach_assignments')
            .delete()
            .eq('drill_id', drillId)
            .eq('position_group_name', positionGroupName);
          
          if (clearError) {
            console.error('Error clearing coach assignments:', clearError);
            toast.error('Failed to clear coach assignments');
            return;
          }
          
          newCoaches = [];
          break;
          
        default:
          toast.error('Invalid action');
          return;
      }
      
      // Update local state after successful database operation
      setDrillCoaches(prev => ({
        ...prev,
        [`${drillId}-${positionGroupName}`]: newCoaches
      }));
      
      const actionText = action === 'add' ? 'added' : action === 'remove' ? 'removed' : 'cleared';
      toast.success(`Coach assignment ${actionText} successfully`);
      
    } catch (error) {
      console.error('Error updating coach assignment:', error);
      toast.error('Failed to update coach assignment');
    }
  };

  // Delete a drill
  const deleteDrill = async (drillId: string) => {
    try {
      const { error } = await supabase
        .from('drills')
        .delete()
        .eq('id', drillId);

      if (error) {
        throw error;
      }

      // Refresh drills after deletion
      if (selectedPractice?.id) {
        await fetchDrills(selectedPractice.id);
      }

      toast.success('Drill deleted successfully');
    } catch (error: any) {
      console.error('Error deleting drill:', error);
      toast.error(`Failed to delete drill: ${error?.message || 'Unknown error'}`);
    }
  };

  // Delete a position group from a drill
  const deletePositionGroup = async (drillId: string, groupName: string) => {
    try {
      // Find the position group in the database
      const { data: positionGroups, error: findError } = await supabase
        .from('position_groups')
        .select('id')
        .eq('drill_id', drillId)
        .eq('group_name', groupName);

      if (findError) {
        throw findError;
      }

      if (!positionGroups || positionGroups.length === 0) {
        // Position group doesn't exist in DB, just update local state
        updateLocalDrillPositionGroups(drillId, groupName);
        toast.success('Position group removed');
        return;
      }

      // Delete all steps and coaching points for each position group
      for (const pg of positionGroups) {
        // Get all step IDs for this position group
        const { data: steps, error: stepsError } = await supabase
          .from('drill_steps')
          .select('id')
          .eq('position_group_id', pg.id);

        if (stepsError) {
          console.error('Error fetching steps:', stepsError);
          continue;
        }

        // Delete coaching points for all steps
        if (steps && steps.length > 0) {
          const stepIds = steps.map(step => step.id);
          await supabase
            .from('drill_coaching_points')
            .delete()
            .in('drill_step_id', stepIds);
        }

        // Delete all steps
        await supabase
          .from('drill_steps')
          .delete()
          .eq('position_group_id', pg.id);
      }

      // Delete the position group(s)
      const { error: deleteError } = await supabase
        .from('position_groups')
        .delete()
        .eq('drill_id', drillId)
        .eq('group_name', groupName);

      if (deleteError) {
        throw deleteError;
      }

      // Also delete coach assignments for this position group
      await supabase
        .from('drill_coach_assignments')
        .delete()
        .eq('drill_id', drillId)
        .eq('position_group_name', groupName);

      // Update local state
      updateLocalDrillPositionGroups(drillId, groupName);

      // Refresh drills to ensure consistency
      if (selectedPractice?.id) {
        await fetchDrills(selectedPractice.id);
      }

      toast.success(`Position group "${groupName}" removed successfully`);
    } catch (error: any) {
      console.error('Error deleting position group:', error);
      toast.error(`Failed to delete position group: ${error?.message || 'Unknown error'}`);
    }
  };

  // Helper function to update local drill state after position group deletion
  const updateLocalDrillPositionGroups = (drillId: string, groupName: string) => {
    setDrills(prevDrills => 
      prevDrills.map(drill => {
        if (drill.id !== drillId) return drill;

        const updatedPositionGroups = (drill.position_groups || []).filter(pg => {
          const pgName = typeof pg === 'string' ? pg : pg.group_name;
          return pgName !== groupName;
        });

        return {
          ...drill,
          position_groups: updatedPositionGroups
        };
      })
    );
  };

  // Save the order of drills
  const saveDrillOrder = async (updatedDrills: Drill[]) => {
    try {
      if (!selectedPractice?.id) {
        toast.error('No practice selected');
        return;
      }

      // Update local state immediately
      setDrills(updatedDrills);

      // Update each drill individually to avoid issues with required fields
      for (let i = 0; i < updatedDrills.length; i++) {
        const drill = updatedDrills[i];
        const { error } = await supabase
          .from('drills')
          .update({ order_index: i })
          .eq('id', drill.id);

        if (error) {
          console.error(`Error updating drill order for ${drill.id}:`, error);
          throw error;
        }
      }
      
      // Removed toast notification as requested - drill order updates should be silent
    } catch (error) {
      console.error('Error updating drill order:', error);
      toast.error('Failed to update drill order');
      // Refresh drills to restore correct order if there was an error
      if (selectedPractice?.id) {
        await fetchDrills(selectedPractice.id);
      }
    }
  };

  // Clear all drills for a practice
  const clearDrills = async () => {
    if (!selectedPractice?.id) return;

    // Show a confirmation toast that requires user interaction
    toast((t) => (
      <div className="flex flex-col gap-2">
        <div className="font-medium">Are you sure you want to clear all drills?</div>
        <div className="text-sm text-gray-500 mb-2">This action cannot be undone.</div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              executeClearDrills();
            }}
            className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete All
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  // Function to actually perform drill deletion
  const executeClearDrills = async () => {
    toast.promise(
      (async () => {
        if (!selectedPractice?.id) {
          throw new Error('No practice selected');
        }
        
        // Delete all drills associated with this practice
        const { error } = await supabase
          .from('drills')
          .delete()
          .eq('practice_id', selectedPractice.id);

        if (error) {
          throw new Error(error.message);
        }

        // Clear local drills state
        setDrills([]);
        
        return 'Drills cleared successfully';
      })(),
      {
        loading: 'Clearing drills...',
        success: 'Drills cleared successfully',
        error: 'Failed to clear drills'
      },
      {
        success: {
          duration: 3000,
        }
      }
    ).then(() => {
      // Clear all notifications after a delay to let users see the success message
      setTimeout(() => {
        toast.dismiss();
      }, 3500); // 3.5 seconds to allow the success message to be visible
    });
  };

  // Enrich a drill with coaching points for editing
  const enrichDrillWithCoachingPoints = async (drill: Drill): Promise<Drill> => {
    if (!drill.id) return drill;
    
    try {
      const coachingPoints = await fetchCoachingPointsForDrill(drill.id);
      
      return {
        ...drill,
        position_groups: drill.position_groups.map((group: any) => ({
          ...group,
          steps: group.steps.map((step: any) => ({
            ...step,
            coaching_points: coachingPoints[step.id] || []
          }))
        }))
      };
    } catch (error) {
      console.error('Error enriching drill with coaching points:', error);
      return drill; // Return original drill if enrichment fails
    }
  };

  // Start editing a drill
  const startEditingDrill = async (drill: Drill) => {
    try {
      setEditingDrill(drill);
      setIsAddingDrill(true);
      
      // Enrich the drill with coaching points
      const enrichedDrill = await enrichDrillWithCoachingPoints(drill);
      setEditingDrill(enrichedDrill);
    } catch (error) {
      console.error('Error starting drill edit:', error);
      toast.error('Failed to load drill for editing');
      // Reset state on error
      setIsAddingDrill(false);
      setEditingDrill(null);
    }
  };

  // Start adding a new drill
  const startAddingDrill = () => {
    setEditingDrill(null);
    setIsAddingDrill(true);
  };

  // Handle drill form close
  const handleDrillFormClose = () => {
    setIsAddingDrill(false);
    setEditingDrill(null);
  };

  // Private helper function to fetch coaching points for a specific drill
  const fetchCoachingPointsForDrill = async (drillId: string): Promise<Record<string, string[]>> => {
    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Coaching points fetch timeout')), 10000); // 10 second timeout
      });

      const fetchPromise = (async () => {
        // Use a more efficient approach - fetch steps and coaching points in a single query with joins
        const { data: stepsWithPoints, error: queryError } = await supabase
          .from('drill_steps')
          .select(`
            id,
            drill_coaching_points (
              point,
              order_index
            )
          `)
          .eq('drill_id', drillId)
          .order('order_index');

        if (queryError) {
          console.error('Error fetching drill steps with coaching points:', queryError);
          return {};
        }

        if (!stepsWithPoints || stepsWithPoints.length === 0) {
          return {};
        }

        // Group coaching points by step ID
        const pointsByStep: Record<string, string[]> = {};
        stepsWithPoints.forEach(step => {
          if (step.drill_coaching_points && step.drill_coaching_points.length > 0) {
            // Sort coaching points by order_index
            const sortedPoints = step.drill_coaching_points
              .sort((a: any, b: any) => (a.order_index || 0) - (b.order_index || 0))
              .map((cp: any) => cp.point);
            pointsByStep[step.id] = sortedPoints;
          } else {
            pointsByStep[step.id] = [];
          }
        });

        return pointsByStep;
      })();

      // Race between the fetch and timeout
      return await Promise.race([fetchPromise, timeoutPromise]);
    } catch (error) {
      console.error('Error fetching coaching points for drill:', error);
      return {};
    }
  };

  return {
    drills,
    isLoading,
    isAddingDrill,
    editingDrill,
    coaches,
    drillCoaches,
    fetchDrills,
    fetchCoaches,
    handleCoachChange,
    deleteDrill,
    deletePositionGroup,
    saveDrillOrder,
    clearDrills,
    startEditingDrill: startEditingDrill as (drill: Drill) => Promise<void>,
    startAddingDrill,
    handleDrillFormClose,
    setDrills
  };
} 